/**
 * NewLearningPage - New Learning Page with full session management
 *
 * Phase 5: Complete integration of all stages
 * - LearningHeader: Logo, back button, course title, lesson title, completion badge
 * - ContentSidebar: Content hierarchy sidebar with resize
 * - LessonOverview: Lesson info and component status cards
 * - ComponentStage: Container for VideoStage/QuizStage
 * - CompletedStage: Completion view with statistics
 *
 * URL: /new-learn/:classProductId/:contentId?
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { learnerApi } from '@/api/b2b'
import { contentApi, type ContentHierarchyNode } from '@/api/content'
import {
    learningSessionApi,
    type LessonDetailsResponse,
    type CompleteSessionResponse,
    type ComponentLocation,
    type ComponentDetail,
    type QuizDataResponse,
    type ModuleActivityType,
} from '@/api/learningSession'
import { aiqApi } from '@/api/aiq'
import { productVideoActivityApi } from '@/api/productVideoActivity'
import { useLearningConfig } from '@/store/learningConfigStore'
import { useAuthStore } from '@/store/authStore'
import { LearningHeader } from '@/components/learner/LearningHeader'
import { LessonOverview } from '@/components/learner/LessonOverview'
import { ModuleOverview } from '@/components/learner/ModuleOverview'
import { ContentSidebar } from '@/components/learner/ContentSidebar'
import { ComponentStage } from '@/components/learner/ComponentStage'
import { CompletedStage } from '@/components/learner/CompletedStage'
import { LessonCompletedStage, type SessionStats } from '@/components/learner/LessonCompletedStage'
import { ModuleQuizCompletedStage } from '@/components/learner/ModuleQuizCompletedStage'
import { QuizReviewStage } from '@/components/learner/QuizReviewStage'
import { QuizStage } from '@/components/learner/QuizStage'
import { MODULE_QUIZ_OPTIONS } from '@/types/quiz'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { cn } from '@/lib/utils'
import { Loader2, BookOpen, Sparkles, CheckCircle } from 'lucide-react'
import aiqBiSvg from '@/assets/aiq-bi.svg'
import { DevToolsPanel } from '@/components/learner/DevToolsPanel'
import { isDevToolsVisible } from '@/utils/devTools'
import { toComponentLocation, findNextComponent, findNextComponentAfter, areAllRequiredCompleted } from '@/utils/learningSequence'

// ============================================================================
// Types
// ============================================================================

type LearningStage = 'overview' | 'learning' | 'completed' | 'lesson-completed' | 'module-quiz' | 'module-quiz-completed' | 'quiz-review'
type LearningMode = 'sequential' | 'individual'

interface ActiveComponent {
    type: 'video' | 'quiz' | 'assignment'
    location: ComponentLocation
}

interface ActiveModuleActivity {
    moduleId: number
    moduleTitle: string
    activityType: ModuleActivityType
    sessionId: number
    componentProgressId: number
    quizData: QuizDataResponse
}

// ============================================================================
// Helper Functions
// ============================================================================

// ============================================================================
// Constants
// ============================================================================

const STALE_TIME_MS = 5 * 60 * 1000 // 5 minutes (페이지 이동 시 재요청 방지)

// ============================================================================
// Component
// ============================================================================

export default function NewLearningPage() {
    const { t } = useTranslation('learner')
    const { classProductId, contentId: urlContentIdParam } = useParams<{
        classProductId: string
        contentId?: string
    }>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()

    // URL moduleId (MyCourses에서 모듈 클릭 시 전달)
    const initialModuleId = searchParams.get('moduleId') ? parseInt(searchParams.get('moduleId')!, 10) : undefined

    // URL contentId
    const urlContentId = urlContentIdParam ? parseInt(urlContentIdParam, 10) : null

    // =========================================================================
    // Sidebar State
    // =========================================================================

    const [isSidebarOpen, setIsSidebarOpen] = useState(true)
    const [sidebarWidth, setSidebarWidth] = useState(320)
    const [isResizing, setIsResizing] = useState(false)

    // =========================================================================
    // Content Selection State
    // =========================================================================

    const [selectedContentId, setSelectedContentId] = useState<number | null>(null)
    const [selectedContentType, setSelectedContentType] = useState<string | null>(null)
    const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null)
    const [parentModuleId, setParentModuleId] = useState<number | null>(null)

    // =========================================================================
    // Session State (Phase 5)
    // =========================================================================

    const [currentStage, setCurrentStage] = useState<LearningStage>('overview')
    const [activeComponent, setActiveComponent] = useState<ActiveComponent | null>(null)
    const [completionData, setCompletionData] = useState<CompleteSessionResponse | null>(null)
    const [activeModuleActivity, setActiveModuleActivity] = useState<ActiveModuleActivity | null>(null)
    const [completedModuleId, setCompletedModuleId] = useState<number | null>(null)
    const [reviewSessionId, setReviewSessionId] = useState<number | null>(null)
    const [componentSessionKey, setComponentSessionKey] = useState(0)
    const [sidebarActivityFocus, setSidebarActivityFocus] = useState<string | null>(null)

    // =========================================================================
    // Sequential Learning State (레슨 진행 플로우)
    // =========================================================================

    const [learningMode, setLearningMode] = useState<LearningMode>('individual')
    const [sessionStats, setSessionStats] = useState<SessionStats>({
        sessionStartTime: new Date(),
        videoWatchedSeconds: 0,
        quizCorrectCount: 0,
        quizTotalCount: 0,
    })
    const [sequentialQueue, setSequentialQueue] = useState<ComponentDetail[]>([])
    const [autoStartNextLesson, setAutoStartNextLesson] = useState(false)

    // =========================================================================
    // Resize Handlers
    // =========================================================================

    const startResizing = useCallback(() => setIsResizing(true), [])
    const stopResizing = useCallback(() => setIsResizing(false), [])

    const resize = useCallback(
        (e: MouseEvent) => {
            if (isResizing) {
                const newWidth = e.clientX
                if (newWidth >= 200 && newWidth <= 600) setSidebarWidth(newWidth)
            }
        },
        [isResizing]
    )

    useEffect(() => {
        window.addEventListener('mousemove', resize)
        window.addEventListener('mouseup', stopResizing)
        return () => {
            window.removeEventListener('mousemove', resize)
            window.removeEventListener('mouseup', stopResizing)
        }
    }, [resize, stopResizing])

    // =========================================================================
    // ESC Key Handler for Focus Mode
    // =========================================================================

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && (currentStage === 'learning' || currentStage === 'module-quiz')) {
                handleBackToOverview()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [currentStage])

    // =========================================================================
    // API Queries
    // =========================================================================

    // 1. Class Course list
    const { data: classCoursesData, isLoading: isLoadingClassCourses } = useQuery({
        queryKey: ['myClassCourses'],
        queryFn: () => learnerApi.getMyClassCourses(),
        staleTime: STALE_TIME_MS,
    })

    const classCourses = classCoursesData?.data?.data || []
    const classCourse = classCourses.find((c) => c.classProductId === Number(classProductId))

    // 1-1. AIQ Status (check if pre-AIQ is required and completed)
    const { data: aiqStatus } = useQuery({
        queryKey: ['aiqStatus', classCourse?.productId, classCourse?.classProductId],
        queryFn: async () => {
            const res = await aiqApi.getStatus(classCourse!.productId, classCourse!.classProductId)
            return res.data.data
        },
        enabled: !!classCourse?.productId,
        staleTime: 30 * 1000,
    })

    // 2. Resolve contentId (URL > classCourse.contentId > bundle first)
    const resolvedContentId = useMemo(() => {
        if (urlContentId) return urlContentId
        if (classCourse?.contentId) return classCourse.contentId
        if (classCourse?.isBundle && classCourse.bundleContents?.[0]?.contentId) {
            return classCourse.bundleContents[0].contentId
        }
        return null
    }, [urlContentId, classCourse])

    // 3. Configuration from global store (cached)
    const { configResponse: configData } = useLearningConfig(
        classCourse?.productId
            ? { productId: classCourse.productId, classId: classCourse.classId }
            : null
    )

    // 4. Auth store for learner name
    const { user } = useAuthStore()

    // 4. Lesson Details (new API)
    const {
        data: lessonDetails,
        isLoading: isLoadingLessonDetails,
        refetch: refetchLessonDetails,
    } = useQuery({
        queryKey: ['lessonDetails', selectedLessonId, classCourse?.classProductId],
        queryFn: async () => {
            if (!selectedLessonId || !classCourse) return null
            return learningSessionApi.getLessonDetails({
                lessonId: selectedLessonId,
                classProductId: classCourse.classProductId,
                productId: classCourse.productId,
                classId: classCourse.classId,
            })
        },
        enabled: !!selectedLessonId && !!classCourse,
        staleTime: 30 * 1000,
    })

    // 5. Module Detail (for ModuleOverview)
    const { data: moduleDetail, isLoading: isLoadingModule } = useQuery({
        queryKey: ['moduleDetail', selectedContentId],
        queryFn: () => contentApi.getModuleDetail(selectedContentId!),
        enabled: !!selectedContentId && selectedContentType === 'module',
    })

    // thumbnailUrl은 lessonDetails에서 직접 사용
    const thumbnailUrl = lessonDetails?.thumbnailUrl || null

    // 7. Completed lesson IDs (for sidebar progress display)
    const { data: completedLessonIds = [], isLoading: isLoadingCompletedLessons, refetch: refetchCompletedLessonIds } = useQuery({
        queryKey: ['completedLessonIds', resolvedContentId, classCourse?.classProductId],
        queryFn: () => learningSessionApi.getCompletedLessonIds(resolvedContentId!, classCourse!.classProductId),
        enabled: !!resolvedContentId && !!classCourse?.classProductId,
        staleTime: 30 * 1000,
    })

    const selectedModuleId = selectedContentType === 'module' ? selectedContentId : null
    const {
        data: moduleActivityStatuses = [],
        refetch: refetchModuleActivityStatuses,
    } = useQuery({
        queryKey: ['moduleActivityStatuses', selectedModuleId, classCourse?.classProductId],
        queryFn: () => learningSessionApi.getModuleActivityStatuses([selectedModuleId!], classCourse?.classProductId),
        enabled: !!selectedModuleId,
        staleTime: 30 * 1000,
    })

    const moduleActivityStatus = moduleActivityStatuses[0]

    // Derived: Check if sequential mode is enabled
    const isSequentialMode =
        configData?.configuration?.completionRules?.lessonSequenceMode === 'sequential'

    // Derived: Get requiredWatchPercent from video configuration
    const requiredWatchPercent = useMemo(() => {
        const videoConfig = configData?.configuration?.sequence?.find(
            (comp: { type: string }) => comp.type.toLowerCase() === 'video'
        )
        return videoConfig?.options?.requiredWatchPercent ?? 90
    }, [configData])

    // Derived: Check if learning can start (pre-AIQ requirement)
    const canStartLearning = aiqStatus?.canStartLearning ?? true // Default true if API not available

    // Derived: Post-AIQ availability
    const canStartPostAiq = aiqStatus?.canStartPostAiq ?? false
    const postAiqCompleted = aiqStatus?.postAiq?.status === 'completed'

    // Derived: Check if start video activity is required but not completed
    const startActivity = configData?.configuration?.productLearning?.startActivity
    const hasStartVideo = startActivity?.enabled === true && (startActivity?.types?.includes('video') ?? false)

    const { data: videoActivityStatus, isFetched: isVideoActivityFetched } = useQuery({
        queryKey: ['productVideoActivity', classCourse?.productId, classCourse?.classProductId],
        queryFn: async () => {
            const res = await productVideoActivityApi.getStatus(classCourse!.productId, classCourse!.classProductId)
            return res.data.data
        },
        enabled: hasStartVideo && !!classCourse?.productId,
        staleTime: 0, // 항상 최신 상태 확인
    })

    // Show modal if start video not completed (fetched 완료 후에만 판단)
    const isVideoGated = hasStartVideo && isVideoActivityFetched && videoActivityStatus?.startVideo?.status !== 'completed'
    const [showVideoGateModal, setShowVideoGateModal] = useState(false)

    useEffect(() => {
        if (isVideoGated) {
            setShowVideoGateModal(true)
        }
    }, [isVideoGated])

    // =========================================================================
    // Content Selection Handler
    // =========================================================================

    const handleContentSelect = useCallback((contentId: number, contentType: string) => {
        setSelectedContentId(contentId)
        setSelectedContentType(contentType)
        setSidebarActivityFocus(null)

        if (contentType === 'lesson') {
            setSelectedLessonId(contentId)
            // Reset session state when selecting new lesson
            setCurrentStage('overview')
            setActiveComponent(null)
            setCompletionData(null)
            setActiveModuleActivity(null)
        } else {
            setSelectedLessonId(null)
            setCurrentStage('overview')
            setActiveComponent(null)
            setCompletionData(null)
            setActiveModuleActivity(null)
        }

        // Close sidebar on mobile
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false)
        }
    }, [])

    const handleLessonSelect = useCallback(
        (lessonId: number, moduleId?: number) => {
            handleContentSelect(lessonId, 'lesson')
            setParentModuleId(moduleId ?? null)
        },
        [handleContentSelect]
    )

    // =========================================================================
    // Sidebar Control (Focus Mode)
    // =========================================================================

    const handleSidebarClose = useCallback(() => {
        setIsSidebarOpen(false)
    }, [])

    const handleSidebarToggle = useCallback(() => {
        setIsSidebarOpen((prev) => !prev)
    }, [])

    // =========================================================================
    // Session Handlers
    // =========================================================================

    /**
     * Start learning session with the first available component
     * Sequential Mode: 레슨 미완료 시 연속 진행, Individual Mode: 개별 진행
     */
    const handleStartSession = useCallback(() => {
        if (!lessonDetails) return

        const enabledComponents = lessonDetails.components.filter((c) => c.enabled)
        if (enabledComponents.length === 0) {
            console.warn('No enabled component found')
            return
        }

        // 레슨이 이미 완료된 경우 Individual Mode
        const isLessonCompleted = lessonDetails.overallStatus === 'completed'

        if (isLessonCompleted) {
            // Individual Mode: 첫 번째 컴포넌트만 시작
            const firstComponent = enabledComponents[0]
            const componentLocation = toComponentLocation(firstComponent.type, firstComponent.location)

            setLearningMode('individual')
            setActiveComponent({
                type: firstComponent.type,
                location: componentLocation,
            })
            setComponentSessionKey(prev => prev + 1)
            setCurrentStage('learning')
        } else {
            // Sequential Mode: 모든 필수 컴포넌트 연속 진행
            const requiredComponents = enabledComponents.filter((c) => c.required)
            const incompleteComponents = requiredComponents.filter(
                (c) => !c.progress || c.progress.status !== 'completed'
            )

            // 미완료 컴포넌트가 없으면 첫 번째 컴포넌트부터 시작
            const componentsToProcess = incompleteComponents.length > 0
                ? incompleteComponents
                : requiredComponents

            if (componentsToProcess.length === 0) {
                console.warn('No components to process')
                return
            }

            const [firstComponent, ...restComponents] = componentsToProcess
            const componentLocation = toComponentLocation(firstComponent.type, firstComponent.location)

            // Sequential Mode 시작: 세션 통계 초기화 및 큐 설정
            setLearningMode('sequential')
            setSessionStats({
                sessionStartTime: new Date(),
                videoWatchedSeconds: 0,
                quizCorrectCount: 0,
                quizTotalCount: 0,
            })
            setSequentialQueue(restComponents)
            setActiveComponent({
                type: firstComponent.type,
                location: componentLocation,
            })
            setComponentSessionKey(prev => prev + 1)
            setCurrentStage('learning')
        }
    }, [lessonDetails])

    // Auto-start next lesson: lessonDetails 로드 완료 시 자동으로 학습 시작
    // lessonDetails.lessonId === selectedLessonId 확인으로 이전 레슨 데이터로 시작하는 레이스 컨디션 방지
    useEffect(() => {
        if (autoStartNextLesson && lessonDetails && !isLoadingLessonDetails
            && lessonDetails.lessonId === selectedLessonId) {
            setAutoStartNextLesson(false)
            handleStartSession()
        }
    }, [autoStartNextLesson, lessonDetails, isLoadingLessonDetails, selectedLessonId, handleStartSession])

    /**
     * Start a specific component
     * 레슨 미완료 시 Sequential Mode, 완료 시 Individual Mode
     */
    const handleStartComponent = useCallback((type: string, location: string) => {
        if (!lessonDetails) return

        const componentType = type as 'video' | 'quiz' | 'assignment'
        const componentLocation = toComponentLocation(type, location)
        const isLessonCompleted = lessonDetails.overallStatus === 'completed'

        if (isLessonCompleted) {
            // Individual Mode: 레슨 완료 후 개별 컴포넌트 진행
            setLearningMode('individual')
            setActiveComponent({
                type: componentType,
                location: componentLocation,
            })
            setComponentSessionKey(prev => prev + 1)
            setCurrentStage('learning')
        } else {
            // Sequential Mode: 레슨 미완료 시 클릭한 컴포넌트부터 연속 진행
            const enabledComponents = lessonDetails.components.filter((c) => c.enabled && c.required)
            const clickedIndex = enabledComponents.findIndex(
                (c) => c.type === type && toComponentLocation(c.type, c.location) === componentLocation
            )

            if (clickedIndex === -1) {
                // 필수 컴포넌트가 아니면 Individual Mode
                setLearningMode('individual')
                setActiveComponent({ type: componentType, location: componentLocation })
                setComponentSessionKey(prev => prev + 1)
                setCurrentStage('learning')
                return
            }

            // 클릭한 컴포넌트부터 나머지 컴포넌트들을 큐에 넣음
            const remainingComponents = enabledComponents.slice(clickedIndex + 1)

            setLearningMode('sequential')
            setSessionStats({
                sessionStartTime: new Date(),
                videoWatchedSeconds: 0,
                quizCorrectCount: 0,
                quizTotalCount: 0,
            })
            setSequentialQueue(remainingComponents)
            setActiveComponent({
                type: componentType,
                location: componentLocation,
            })
            setComponentSessionKey(prev => prev + 1)
            setCurrentStage('learning')
        }
    }, [lessonDetails])

    /**
     * Start module activity (start/end)
     */
    const handleStartModuleActivity = useCallback(
        async (moduleId: number, activityType: ModuleActivityType) => {
            if (!classCourse) return

            handleContentSelect(moduleId, 'module')

            try {
                const activityConfig = activityType === 'start'
                    ? configData?.configuration?.moduleLearning?.startActivity
                    : configData?.configuration?.moduleLearning?.endActivity

                const response = await learningSessionApi.startModuleActivity({
                    moduleId,
                    activityType,
                    classProductId: classCourse.classProductId,
                    questionCount: activityConfig?.quizOptions?.questionCount,
                    shuffleQuestions: activityConfig?.quizOptions?.shuffleQuestions,
                })

                if (!response.quizData || response.sessionId == null) {
                    toast.warning(t('courses.noModuleQuestions'))
                    return
                }

                const detail = moduleDetail?.id === moduleId
                    ? moduleDetail
                    : await contentApi.getModuleDetail(moduleId)

                const moduleTitle = detail.bareTitle || detail.title

                setActiveModuleActivity({
                    moduleId,
                    moduleTitle,
                    activityType,
                    sessionId: response.sessionId,
                    componentProgressId: response.componentProgressId,
                    quizData: response.quizData,
                })
                setCurrentStage('module-quiz')
                handleSidebarClose()
            } catch (error) {
                console.error('Failed to start module activity:', error)
                toast.error(t('courses.moduleActivityFailed'))
            }
        },
        [classCourse, configData, handleContentSelect, moduleDetail, handleSidebarClose]
    )

    /**
     * Handle component completion
     * Sequential Mode: 완료 화면 없이 다음 컴포넌트로 자동 진행
     * Individual Mode: Overview로 복귀
     */
    const handleComponentComplete = useCallback(
        async (response: CompleteSessionResponse) => {
            setCompletionData(response)

            // 세션 통계 업데이트 (누적)
            if (activeComponent?.type === 'video') {
                setSessionStats((prev) => ({
                    ...prev,
                    videoWatchedSeconds: prev.videoWatchedSeconds + (response.totalWatchTimeSec || 0),
                }))
            } else if (activeComponent?.type === 'quiz' || activeComponent?.type === 'assignment') {
                // 퀴즈/과제 결과 누적
                setSessionStats((prev) => ({
                    ...prev,
                    quizCorrectCount: prev.quizCorrectCount + (response.correctCount || 0),
                    quizTotalCount: prev.quizTotalCount + (response.totalQuestions || 0),
                }))
            }

            // Sequential Mode vs Individual Mode 분기
            if (learningMode === 'sequential') {
                // Sequential Mode: 다음 컴포넌트로 자동 진행
                if (sequentialQueue.length > 0) {
                    // 다음 컴포넌트가 있으면 완료 화면 없이 바로 진행
                    const [nextComponent, ...restQueue] = sequentialQueue
                    const componentLocation = toComponentLocation(nextComponent.type, nextComponent.location)

                    // 중간 컴포넌트는 fire-and-forget으로 refetch
                    refetchLessonDetails()
                    refetchCompletedLessonIds()

                    setSequentialQueue(restQueue)
                    setActiveComponent({
                        type: nextComponent.type,
                        location: componentLocation,
                    })
                    // currentStage는 'learning' 유지 (완료 화면 없음)
                } else {
                    // 마지막 컴포넌트 완료 → 레슨 완료 화면
                    // getLessonDetails가 reconciliation을 먼저 수행한 후 completedLessonIds 조회
                    await refetchLessonDetails()
                    await refetchCompletedLessonIds()
                    // progressSummary 갱신을 위해 classCourses 쿼리 무효화 (현재 레슨 포함)
                    queryClient.invalidateQueries({ queryKey: ['myClassCourses'] })
                    setCurrentStage('lesson-completed')
                }
            } else {
                // Individual Mode: Overview로 복귀 (인라인 처리)
                // getLessonDetails가 reconciliation을 먼저 수행한 후 completedLessonIds 조회
                await refetchLessonDetails()
                await refetchCompletedLessonIds()
                setCurrentStage('overview')
                setActiveComponent(null)
                setCompletionData(null)
                setActiveModuleActivity(null)
                setIsSidebarOpen(true)
            }
        },
        [activeComponent, learningMode, sequentialQueue, refetchLessonDetails, refetchCompletedLessonIds, queryClient]
    )

    /**
     * Handle module activity completion
     * - 모듈 마무리 퀴즈(end): ModuleQuizCompletedStage로 이동
     * - 모듈 시작 퀴즈(start): 기존 CompletedStage 사용
     */
    const handleModuleActivityComplete = useCallback(
        async (response: CompleteSessionResponse) => {
            // 모듈 활동 상태 갱신
            await refetchModuleActivityStatuses()
            queryClient.invalidateQueries({ queryKey: ['moduleActivityStatuses'] })
            queryClient.invalidateQueries({ queryKey: ['moduleQuizCompletionSummary'] })
            queryClient.invalidateQueries({ queryKey: ['aiqStatus'] })

            // 모듈 마무리 퀴즈인 경우 전용 완료 화면으로 이동
            if (activeModuleActivity?.activityType === 'end') {
                // 순서 중요: stage를 먼저 변경해야 중간에 ModuleOverview가 깜빡이지 않음
                setCurrentStage('module-quiz-completed')
                setCompletedModuleId(activeModuleActivity.moduleId)
                setActiveModuleActivity(null)
                return
            }

            // 모듈 시작 퀴즈인 경우 기존 완료 화면 사용
            setCompletionData(response)
            setActiveComponent({
                type: 'quiz',
                location: 'module_start_quiz',
            })
            setCurrentStage('completed')
        },
        [queryClient, refetchModuleActivityStatuses, activeModuleActivity]
    )

    /**
     * Back to module overview (from module quiz)
     */
    const handleBackToModuleOverview = useCallback(() => {
        setCurrentStage('overview')
        setActiveModuleActivity(null)
        // Re-open sidebar when returning to overview
        setIsSidebarOpen(true)
    }, [])

    /**
     * Go back to overview
     */
    const handleBackToOverview = useCallback(() => {
        setCurrentStage('overview')
        setActiveComponent(null)
        setCompletionData(null)
        setActiveModuleActivity(null)
        // Re-open sidebar when returning to overview
        setIsSidebarOpen(true)
        // Refresh lesson details and completed lesson IDs for sidebar lock state
        if (selectedLessonId) {
            refetchLessonDetails()
        }
        refetchCompletedLessonIds()
    }, [refetchLessonDetails, refetchCompletedLessonIds, selectedLessonId])

    /**
     * Restart the current component
     */
    const handleRestart = useCallback(() => {
        if (activeComponent) {
            // Re-enter learning stage with same component
            setCurrentStage('learning')
            setCompletionData(null)
        }
    }, [activeComponent])

    /**
     * Navigate to next lesson
     * LessonCompletedStage에서 전달받은 nextLessonId 사용
     */
    const handleNextLesson = useCallback((nextLessonId: number) => {
        // 완료된 레슨 목록 갱신 (사이드바 잠금 상태 업데이트)
        refetchCompletedLessonIds()
        // 다음 레슨 선택 및 자동 시작 플래그 설정
        handleContentSelect(nextLessonId, 'lesson')
        setAutoStartNextLesson(true)
        // 사이드바 닫기 (학습 모드 진입 준비)
        setIsSidebarOpen(false)
    }, [handleContentSelect, refetchCompletedLessonIds])

    /**
     * Navigate to module checkup quiz
     * 모듈 마무리 퀴즈로 이동: ModuleOverview 표시 및 sidebar 오픈
     */
    const handleModuleCheckupQuiz = useCallback((moduleId: number) => {
        handleContentSelect(moduleId, 'module')
        setSidebarActivityFocus(`${moduleId}-end`)
        setIsSidebarOpen(true)
    }, [handleContentSelect])

    /**
     * Navigate to course list
     */
    const handleNavigateToList = useCallback(() => {
        navigate('/my/courses')
    }, [navigate])

    // =========================================================================
    // Module Quiz Completed Stage Handlers
    // =========================================================================

    /**
     * 모듈 퀴즈 채점 결과 보기 (퀴즈 리뷰 모드)
     */
    const handleViewQuizResults = useCallback((sessionId: number, _componentProgressId: number) => {
        setReviewSessionId(sessionId)
        setCurrentStage('quiz-review')
    }, [])

    /**
     * 모듈 퀴즈 실패 후 복습하기 (모듈 첫 레슨으로 이동)
     */
    const handleModuleQuizReview = useCallback((lessonId: number) => {
        handleContentSelect(lessonId, 'lesson')
        setCompletedModuleId(null)
        setIsSidebarOpen(true)
    }, [handleContentSelect])

    /**
     * 모듈 퀴즈 재도전 (ModuleOverview로 이동)
     */
    const handleModuleQuizRetry = useCallback(() => {
        if (completedModuleId) {
            queryClient.invalidateQueries({ queryKey: ['moduleQuizCompletionSummary'] })
            handleContentSelect(completedModuleId, 'module')
            setCompletedModuleId(null)
            setIsSidebarOpen(true)
        }
    }, [completedModuleId, handleContentSelect, queryClient])

    /**
     * AIQ 페이지로 이동
     */
    const handleNavigateToAiq = useCallback(() => {
        navigate('/my/aiq')
    }, [navigate])

    /**
     * 수강 완료 확인 (Post-AIQ 가능하면 AIQ 페이지로, 아니면 목록으로)
     */
    const handleCourseComplete = useCallback(() => {
        if (canStartPostAiq && !postAiqCompleted) {
            navigate('/my/aiq')
        } else {
            navigate('/my/courses')
        }
    }, [navigate, canStartPostAiq, postAiqCompleted])

    // =========================================================================
    // Render: Loading State
    // =========================================================================

    if (isLoadingClassCourses) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <Loader2 className="mx-auto mb-4 w-10 h-10 animate-spin text-primary-600" />
                    <p className="text-gray-500">Loading content...</p>
                </div>
            </div>
        )
    }

    // =========================================================================
    // Render: Class Course Not Found
    // =========================================================================

    if (!classCourse) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-gray-700">
                        Course not found
                    </p>
                    <p className="mb-4 text-sm text-gray-500">
                        No course found for classProductId: {classProductId}
                    </p>
                    <button
                        onClick={() => navigate('/my/courses')}
                        className="font-medium text-primary-600 hover:text-primary-700"
                    >
                        Go to My Courses
                    </button>
                </div>
            </div>
        )
    }

    // =========================================================================
    // Render: Bundle without contentId
    // =========================================================================

    if (classCourse.isBundle && !resolvedContentId) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-gray-700">
                        Content not found
                    </p>
                    <p className="mb-4 text-sm text-gray-500">
                        Bundle products require a contentId.
                    </p>
                    <button
                        onClick={() => navigate('/my/courses')}
                        className="font-medium text-primary-600 hover:text-primary-700"
                    >
                        Go to My Courses
                    </button>
                </div>
            </div>
        )
    }

    // =========================================================================
    // Derived Values
    // =========================================================================

    const bundleContent = classCourse.isBundle && resolvedContentId
        ? classCourse.bundleContents?.find((c) => c.contentId === resolvedContentId)
        : undefined
    const rootContentType = bundleContent?.contentType || classCourse.contentType
    const courseTitle = bundleContent?.contentTitle || classCourse.contentTitle || `Content #${resolvedContentId}`
    const lessonTitle = lessonDetails?.title

    // Determine if there's a next lesson based on nextAction
    const hasNextLesson = !!(classCourse?.nextAction?.contentId && classCourse.nextAction.contentId !== selectedLessonId)

    // =========================================================================
    // Render: Main Layout
    // =========================================================================

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <LearningHeader
                onToggleSidebar={handleSidebarToggle}
                courseTitle={courseTitle}
                contentType={rootContentType}
                lessonTitle={lessonTitle}
                isCompleted={classCourse.isCompleted}
            />

            <div className="flex h-[calc(100vh-60px)] relative">
                {/* Sidebar */}
                <div
                    className={cn(
                        'relative z-40 h-full bg-gray-50 transition-all duration-300 ease-in-out shrink-0',
                        'absolute inset-y-0 left-0 shadow-lg',
                        'lg:static lg:shadow-none',
                        isSidebarOpen
                            ? 'border-r border-gray-200 translate-x-0'
                            : '-translate-x-full lg:w-0 lg:translate-x-0 lg:border-r-0 lg:overflow-hidden'
                    )}
                    style={{
                        width: isSidebarOpen
                            ? window.innerWidth >= 1024
                                ? sidebarWidth
                                : 320
                            : undefined,
                    }}
                >
                    <div
                        className="h-full"
                        style={{ width: window.innerWidth >= 1024 ? sidebarWidth : 320 }}
                    >
                        <ContentSidebar
                            contentId={resolvedContentId!}
                            currentLessonId={selectedLessonId || undefined}
                            currentContentId={selectedContentId || undefined}
                            currentContentType={selectedContentType || undefined}
                            onLessonSelect={handleLessonSelect}
                            onContentSelect={handleContentSelect}
                            onModuleActivitySelect={handleStartModuleActivity}
                            className="w-full h-full border-none"
                            lessonSequenceMode={
                                configData?.configuration?.completionRules?.lessonSequenceMode ||
                                'sequential'
                            }
                            completedLessonIds={completedLessonIds}
                            isLoadingCompletedLessons={isLoadingCompletedLessons}
                            resumeLessonId={classCourse?.nextAction?.contentId}
                            initialModuleId={initialModuleId}
                            moduleLearning={configData ? (configData.configuration?.moduleLearning ?? null) : undefined}
                            classProductId={classCourse?.classProductId}
                            activeModuleActivityKey={
                                activeModuleActivity
                                    ? `${activeModuleActivity.moduleId}-${activeModuleActivity.activityType}`
                                    : currentStage === 'module-quiz-completed' && completedModuleId
                                        ? `${completedModuleId}-end`
                                        : sidebarActivityFocus
                            }
                        />
                    </div>

                    {/* Resize Handle */}
                    {isSidebarOpen && (
                        <div
                            className="hidden absolute top-0 right-0 bottom-0 z-50 w-1 transition-colors cursor-col-resize hover:bg-indigo-400 active:bg-indigo-600 lg:block"
                            onMouseDown={startResizing}
                        />
                    )}
                </div>

                {/* Main Content */}
                <main className="overflow-y-auto flex-1 w-full">
                    <div className="p-6 mx-auto max-w-5xl">
                        {/* Post-AIQ Guidance Banner - 상단 고정 */}
                        {currentStage === 'overview' && canStartPostAiq && !postAiqCompleted && (
                            <div className="flex flex-col gap-3 items-center p-5 mb-6 bg-blue-50 rounded-2xl border border-blue-200 sm:flex-row sm:justify-between">
                                <div className="flex gap-3 items-center">
                                    <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-xl">
                                        <Sparkles className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-blue-700">{t('courses.postAiqAvailable')}</p>
                                        <p className="text-sm text-blue-600">{t('courses.postAiqAvailableDesc')}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleNavigateToAiq}
                                    className="flex gap-2 items-center px-5 py-2 font-medium text-white bg-blue-600 rounded-lg transition-colors shrink-0 hover:bg-blue-700"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {t('courses.goToTest')}
                                </button>
                            </div>
                        )}

                        {/* Post-AIQ 완료 안내 - 상단 고정 */}
                        {currentStage === 'overview' && postAiqCompleted && (
                            <div className="flex gap-3 items-center p-4 mb-6 bg-emerald-50 rounded-xl border border-emerald-200">
                                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                                <p className="text-sm font-medium text-emerald-700">
                                    {t('courses.postAiqCompleted')}
                                </p>
                            </div>
                        )}

                        {/* Stage: Overview */}
                        {currentStage === 'overview' && selectedContentType === 'lesson' && lessonDetails && (
                            <LessonOverview
                                lessonDetails={lessonDetails}
                                thumbnailUrl={thumbnailUrl}
                                onStart={handleStartSession}
                                onStartComponent={handleStartComponent}
                                isLoading={isLoadingLessonDetails}
                                isSequentialMode={isSequentialMode}
                                canStartLearning={canStartLearning}
                                onNavigateToAiq={handleNavigateToAiq}
                            />
                        )}

                        {currentStage === 'overview' && selectedContentType === 'module' && moduleDetail && (
                            <ModuleOverview
                                moduleDetail={moduleDetail}
                                completedLessonIds={completedLessonIds}
                                moduleLearning={configData ? (configData.configuration?.moduleLearning ?? null) : undefined}
                                moduleActivityStatus={moduleActivityStatus}
                                learnerName={user?.fullName}
                                onStartActivity={(type) => handleStartModuleActivity(moduleDetail.id, type)}
                            />
                        )}

                        {/* Stage: Learning (ComponentStage) */}
                        {currentStage === 'learning' &&
                            selectedLessonId &&
                            activeComponent &&
                            classCourse && (
                                <ComponentStage
                                    key={`${selectedLessonId}-${activeComponent.location}-${componentSessionKey}`}
                                    lessonId={selectedLessonId}
                                    lessonTitle={lessonTitle || 'Lesson'}
                                    componentType={activeComponent.type}
                                    componentLocation={activeComponent.location}
                                    classProductId={classCourse.classProductId}
                                    requiredWatchPercent={requiredWatchPercent}
                                    onComplete={handleComponentComplete}
                                    onBack={handleBackToOverview}
                                    onSidebarClose={handleSidebarClose}
                                />
                            )}

                        {/* Stage: Module Activity Quiz */}
                        {currentStage === 'module-quiz' && activeModuleActivity && classCourse && (() => {
                            // Configuration에서 활동 유형에 따른 quizOptions 가져오기
                            const activityConfig = activeModuleActivity.activityType === 'start'
                                ? configData?.configuration?.moduleLearning?.startActivity
                                : configData?.configuration?.moduleLearning?.endActivity
                            const configPassingScore = activityConfig?.quizOptions?.passingScore

                            // Configuration의 passingScore가 있으면 사용, 없으면 MODULE_QUIZ_OPTIONS 기본값 사용
                            const behaviorOptions = {
                                ...MODULE_QUIZ_OPTIONS,
                                passScore: configPassingScore ?? MODULE_QUIZ_OPTIONS.passScore,
                            }

                            return (
                                <QuizStage
                                    lessonId={activeModuleActivity.moduleId}
                                    lessonTitle={activeModuleActivity.moduleTitle}
                                    componentProgressId={activeModuleActivity.componentProgressId}
                                    sessionId={activeModuleActivity.sessionId}
                                    quizData={activeModuleActivity.quizData}
                                    quizLocation="checkup"
                                    classProductId={classCourse.classProductId}
                                    behaviorOptions={behaviorOptions}
                                    onComplete={handleModuleActivityComplete}
                                    onBack={handleBackToModuleOverview}
                                    skipCompletionScreen={true}
                                />
                            )
                        })()}

                        {/* Stage: Completed (Individual Mode용 - 현재 사용 안 함) */}
                        {currentStage === 'completed' &&
                            completionData &&
                            activeComponent && (
                                <CompletedStage
                                    lessonTitle={lessonTitle || 'Lesson'}
                                    componentType={activeComponent.type}
                                    completionData={completionData}
                                    hasNextLesson={hasNextLesson}
                                    onRestart={handleRestart}
                                    onNextLesson={hasNextLesson && classCourse?.nextAction?.contentId
                                        ? () => handleNextLesson(classCourse.nextAction!.contentId)
                                        : undefined}
                                    onBackToOverview={handleBackToOverview}
                                    onNavigateToList={handleNavigateToList}
                                />
                            )}

                        {/* Stage: Lesson Completed (Sequential Mode 완료 후) */}
                        {currentStage === 'lesson-completed' && selectedLessonId && classCourse && (
                            <LessonCompletedStage
                                lessonId={selectedLessonId}
                                classProductId={classCourse.classProductId}
                                progressSummary={classCourse.progressSummary}
                                onBackToOverview={handleBackToOverview}
                                onNextLesson={handleNextLesson}
                                onModuleCheckupQuiz={handleModuleCheckupQuiz}
                            />
                        )}

                        {/* Stage: Module Quiz Completed (모듈 마무리 퀴즈 완료 후) */}
                        {currentStage === 'module-quiz-completed' && completedModuleId && classCourse && (
                            <ModuleQuizCompletedStage
                                moduleId={completedModuleId}
                                learnerName={user?.fullName}
                                classProductId={classCourse.classProductId}
                                onViewResults={handleViewQuizResults}
                                onNextLesson={handleNextLesson}
                                onReview={handleModuleQuizReview}
                                onRetry={handleModuleQuizRetry}
                                onBackToModule={handleBackToOverview}
                                onCourseComplete={handleCourseComplete}
                                canStartPostAiq={canStartPostAiq}
                                postAiqCompleted={postAiqCompleted}
                                onNavigateToAiq={handleNavigateToAiq}
                            />
                        )}

                        {/* Stage: Quiz Review (채점 결과 보기) */}
                        {currentStage === 'quiz-review' && reviewSessionId && (
                            <QuizReviewStage
                                sessionId={reviewSessionId}
                                onBack={() => {
                                    setCurrentStage('module-quiz-completed')
                                    setReviewSessionId(null)
                                }}
                            />
                        )}

                        {/* Loading state for lesson/module details */}
                        {currentStage === 'overview' && (isLoadingLessonDetails || isLoadingModule) && (
                            <div className="flex justify-center items-center py-20">
                                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                            </div>
                        )}

                        {/* No lesson selected */}
                        {currentStage === 'overview' &&
                            !isLoadingLessonDetails &&
                            !isLoadingModule &&
                            (!selectedContentType ||
                                (selectedContentType === 'lesson' && !lessonDetails) ||
                                (selectedContentType === 'module' && !moduleDetail)) && (
                                <div className="py-20 text-center">
                                    <BookOpen className="mx-auto mb-4 w-12 h-12 text-gray-400" />
                                    <p className="text-gray-500">
                                        Please select a lesson or module from the sidebar
                                    </p>
                                </div>
                            )}
                    </div>
                </main>
            </div>

            {/* Dev Tools Panel */}
            {isDevToolsVisible(user?.email) && (
                <DevToolsPanel
                    selectedLessonId={selectedLessonId}
                    selectedContentType={selectedContentType}
                    selectedModuleId={selectedModuleId ?? parentModuleId}
                    resolvedContentId={resolvedContentId}
                    classProductId={classCourse.classProductId}
                    productId={classCourse.productId}
                    classId={classCourse.classId}
                    onRefresh={() => {
                        refetchLessonDetails()
                        refetchCompletedLessonIds()
                        refetchModuleActivityStatuses()
                        queryClient.invalidateQueries({ queryKey: ['myClassCourses'] })
                    }}
                />
            )}

            {/* Video Gate Modal */}
            <Dialog open={showVideoGateModal} onOpenChange={(open) => { if (!open) navigate('/my/courses', { replace: true }) }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('courses.startVideoRequired')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-6 items-center py-4 text-center">
                        <img src={aiqBiSvg} alt="AIQ" className="h-10" />
                        <p className="text-sm text-gray-600 whitespace-pre-line">{t('courses.startVideoRequiredDesc', { name: user?.fullName })}</p>
                        <button
                            onClick={() => navigate('/my/courses', { replace: true })}
                            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
                        >
                            {t('courses.goToCourses')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
